import React, { useEffect, useState } from "react";
import styles from "./ProfilePermissions.module.css";
import { motion } from "framer-motion";
import request from "../../utils/request";
import InputQ from "../../components/FormComponents/InputQ/InputQ";
import t from "../../utils/translation";
import SpinnerQ from "../../components/SpinnerQ/SpinnerQ";
import { toast } from "react-toastify";
import SwitchQ from "../../components/Switch/SwitchQ";
import { getUserId, getUserType } from "../../utils/sessionHandler";
import Swal from "sweetalert2";

// React Icons
import { MdSave, MdDelete, MdCancel } from "react-icons/md";
import Redirect from "../../utils/Redirect";

export default function ProfilePermissions() {
  const [screenPermissions, setScreenPermissions] = useState([]);
  const [sections, setSections] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const res = await request().get("/Api/Profile/list");
      setProfiles(res.data);
    } catch (error) {
      toast.error("Error loading profiles");
    } finally {
      setLoading(false);
    }
  };

  const getSections = async () => {
    setLoading(true);
    try {
      const res = await request().get("/api/ScreenPermissions");
      setSections(res.data);
    } catch (error) {
      toast.error("Error loading sections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (getUserType() === 2) {
      toast.info("Operator users are not allowed to manage this screen.");
      Redirect({
        redirect: true,
        path: "/appmain",
      });
      return;
    }

    fetchProfiles();
    getSections();
  }, []);

  const selectProfile = async (id) => {
    setLoadingEdit(true);
    try {
      const res = await request().get(`/api/profile/ById?profileId=${id}`);
      const profile = res.data;

      setSelectedProfile(profile);
      setName(profile.name);
      setIsEditing(true);

      const permissions = profile.userScreenPermissions.map(
        (p) => p.screenPermissionId
      );
      setScreenPermissions(permissions);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error loading profile");
    } finally {
      setLoadingEdit(false);
    }
  };

  const cancelEditing = () => {
    setSelectedProfile(null);
    setName("");
    setScreenPermissions([]);
    setIsEditing(false);
  };

  const togglePermission = (permissionId) => {
    setScreenPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const toggleAll = (screen) => {
    const allPermissionIds = screen.permissions.map((p) => p.id);
    setScreenPermissions((prev) =>
      allPermissionIds.some((id) => prev.includes(id))
        ? prev.filter((id) => !allPermissionIds.includes(id))
        : [...prev, ...allPermissionIds]
    );
  };

  const saveProfile = async () => {
    if (!name.trim()) {
      toast.error(t("NameRequired"));
      return;
    }

    setLoading(true);
    try {
      if (isEditing && selectedProfile?.id) {
        await request().put(`/api/profile?id=${selectedProfile.id}`, {
          name,
          screenPermissions,
        });
        const token = await request().get(
          `/api/UserScreenPermissions?AppUserId=${getUserId()}`
        );
        localStorage.setItem("permissions", token.data ?? null);

        toast.success(t("BlankEntry119"));
      } else {
        await request().post("/api/profile", {
          name,
          screenPermissions,
        });
        toast.success(t("BlankEntry120"));
      }
      cancelEditing();
      fetchProfiles();
    } catch (error) {
      toast.error("Error saving profile");
    } finally {
      setLoading(false);
    }
  };

  const deleteProfile = async () => {
    if (!selectedProfile?.id) return;

    try {
      await request().delete(`/api/profile?profileId=${selectedProfile.id}`);
      toast.success(t("BlankEntry121"));
      cancelEditing();
      fetchProfiles();
    } catch (error) {
      toast.error("Error deleting profile");
    }
  };

  const handleDeleteConfirmation = () => {
    Swal.fire({
      title: t("DeleteConfirmationTitle"),
      text: t("DeleteConfirmationText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("BlankEntry122"),
    }).then((result) => {
      if (result.isConfirmed) {
        deleteProfile();
      }
    });
  };

  if (getUserType() === 2) return null;

  return (
    <div className={styles.mainContainer}>
      <h1 className={styles.mainHeader}>{t("BlankEntry118")}</h1>

      {loading ? (
        <div className="spinnerContainer">
          <SpinnerQ />
        </div>
      ) : (
        <>
          <div className={styles.sectionHeader}>
            <h2>{t("BlankEntry123")}</h2>
          </div>
          <div className={styles.profileList}>
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className={`${styles.profileItem} ${
                  isEditing && selectedProfile?.id === profile.id
                    ? styles.profileItemActive
                    : ""
                }`}
                onClick={() => selectProfile(profile.id)}
              >
                {profile.name}
              </div>
            ))}
          </div>

          <div className={styles.formHeader}>
            <h2>{isEditing ? t("BlankEntry124") : t("BlankEntry125")}</h2>
          </div>

          {loadingEdit ? (
            <div className="spinnerContainer">
              <SpinnerQ />
            </div>
          ) : (
            <div className={styles.container}>
              <InputQ
                label={t("NameSpan") || "Name"}
                handleChange={(e) => setName(e.target.value)}
                value={name}
                containerWidth="350px"
                required
              />

              <div className={styles.grid}>
                {sections.map((screen) => (
                  <div key={screen.screenId} className={styles.card}>
                    <div className={styles.cardContent}>
                      <motion.div className={styles.actionHeader}>
                        <h3 className={styles.sectionTitle}>{screen.screen}</h3>
                        <SwitchQ
                          checked={screen.permissions.some((perm) =>
                            screenPermissions.includes(perm.id)
                          )}
                          onChange={() => toggleAll(screen)}
                        />
                      </motion.div>
                      {screen.permissions.map((perm) => (
                        <motion.div key={perm.id} className={styles.actionItem}>
                          <span>{perm.permission}</span>
                          <SwitchQ
                            checked={screenPermissions.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                          />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.buttonContainer}>
                <button className={styles.saveButton} onClick={saveProfile}>
                  <MdSave style={{ marginRight: "5px" }} />
                  {t("Save")}
                </button>
                {isEditing && (
                  <>
                    <button
                      className={styles.cancelButton}
                      onClick={cancelEditing}
                    >
                      <MdCancel style={{ marginRight: "5px" }} />
                      {t("Cancel")}
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={handleDeleteConfirmation}
                    >
                      <MdDelete style={{ marginRight: "5px" }} />
                      {t("DeleteText")}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
